const GlobalVariables = require("./globalVariables");
const whiteLabelConfig = require("./whiteLabelConfig");

class UserModel {
  static getEndpoints() {
    return {
      trainedAssistants: "/api/user/trained-assistants-new",
      defaultAssistant: "/api/user/default-assistant",
      organizationDefaultAssistant: "/api/user/organization-default-assistant",
    };
  }

  constructor(uid, token, email, clientHostname, organizationId = null) {
    this.uid = uid;
    this.token = token;
    this.email = email;
    this.organizationId = organizationId;
    this.assistants = []; // Initialize the assistants array in the state
    this.clientHostname = clientHostname;
    this.whiteLabel = whiteLabelConfig[clientHostname];
    if (
      this.email?.includes("assetline.com.au") ||
      this.email?.includes("altx.com.au")
    ) {
      this.whiteLabel = whiteLabelConfig["assetline.addy.so"];
    }

    const defaultAssistants = [
      {
        appID: "addy-default-assistant-c66e0609-371e-49b2-bb4a-5186526b2a9d",
        appImageURL: "",
        appName:
          this.whiteLabel && this.whiteLabel.appName
            ? this.whiteLabel.appName
            : "Addy AI",
        appTitle: "Trained to answer mortgage lending questions",
        appType: "default",
        creatorName: "Addy AI",
        description: "Trained to answer mortgage lending questions",
        trainingStatus: "completed",
        trained: true,
        organizationId: "",
        organizationName: "Default",
        backgroundResponse: false,
        publicId: "1123cacb-2789-4531-936c-8c9b8c94b647", // Default public ID for Addy AI
      },
    ];
    // Addy AI v2 (v2 knowledge base collection) only for Addy staff
    const normalizedEmail = this.email?.toLowerCase() || "";
    if (
      normalizedEmail.endsWith("@addy-ai.com") ||
      normalizedEmail.endsWith("@addy.com")
    ) {
      defaultAssistants.push({
        appID: "addy-default-assistant-c66e0609-371e-49b2-bb4a-5186526b2a9d-v2",
        appImageURL: "",
        appName: "Addy AI v2",
        appTitle: "Trained to answer mortgage lending questions (v2 KB)",
        appType: "default",
        creatorName: "Addy AI",
        description:
          "Same as Addy AI but uses v2 knowledge base collection (testing).",
        traningStatus: "complete",
        organizationId: "",
        organizationName: "Default",
        backgroundResponse: false,
        publicId: "1123cacb-2789-4531-936c-8c9b8c94b647",
      });
    }
    this.defaultAssistants = defaultAssistants;
  }

  isValid() {
    return this.uid !== "" && this.token !== "";
  }

  async getAssistants(
    mode = "",
    loanId = "",
    requestedAppId = "",
    authToken = this.token
  ) {
    const API_URL_STAUTS =
      GlobalVariables.getAPIURL() + UserModel.getEndpoints().trainedAssistants;
    try {
      // Build query params, including organizationId if available
      const queryParams = new URLSearchParams({
        uid: this.uid,
        authEmail: this.email,
      });

      if (this.organizationId) {
        queryParams.append("organizationId", this.organizationId);
      }
      if (mode) {
        queryParams.append("mode", mode);
      }
      if (loanId) {
        queryParams.append("loanId", loanId);
      }
      if (requestedAppId) {
        queryParams.append("appID", requestedAppId);
      }

      const response = await fetch(
        `${API_URL_STAUTS}?${queryParams.toString()}`,
        {
          method: "GET",
          headers: {
            ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
          },
        }
      );

      if (!response.ok) {
        console.warn(
          `Failed to fetch assistants: ${response.status} ${response.statusText}`
        );
        return this.defaultAssistants;
      }

      const data = await response.json();

      // Handle case where assistants array is missing or invalid
      if (!data.assistants || !Array.isArray(data.assistants)) {
        console.warn("Invalid assistants response, using defaults");
        return this.defaultAssistants;
      }
      this.resolvedPortalAssistantId = data.selectedAssistantId || null;

      // Combine default assistants with fetched assistants
      // this.assistants = this.defaultAssistants.concat(data.assistants);
      let returnedAssistants = data.assistants;

      if (this.whiteLabel && mode === "documentPortal") {
        returnedAssistants = returnedAssistants.map((assistant) =>
          assistant.appID ===
          "addy-default-assistant-c66e0609-371e-49b2-bb4a-5186526b2a9d"
            ? { ...assistant, appName: this.defaultAssistants[0].appName }
            : assistant
        );
      } else if (this.whiteLabel) {
        // Filter returned assistants to remove all assistants with appID prefix "addy-default-assistant"
        returnedAssistants = returnedAssistants.filter((assistant) => {
          return !assistant.appID.startsWith("addy-default-assistant");
        });
        const fannieMaeId =
          "addy-default-assistant-2d7053ab-1674-4e26-9660-ed2c46c6241d";
        const freddieMacId =
          "addy-default-assistant-d17766d1-d0b3-4f4e-b04e-6564c5d334d4";

        if (this.whiteLabel?.theme === "nouri") {
          // Append Fannie Mae and Freddie Mac assistants to the returned assistants
          const fannieMaeAssistant = data.assistants.find(
            (assistant) => assistant.appID === fannieMaeId
          );
          const freddieMacAssistant = data.assistants.find(
            (assistant) => assistant.appID === freddieMacId
          );
          if (fannieMaeAssistant) returnedAssistants.push(fannieMaeAssistant);
          if (freddieMacAssistant) returnedAssistants.push(freddieMacAssistant);
        }
      }
      this.assistants = returnedAssistants;
      return this.assistants;
    } catch (error) {
      // Log as warning instead of error for network failures during local development
      // This is expected when the backend is not running
      if (error.message === "Failed to fetch") {
        console.warn(
          "Unable to fetch assistants - backend may not be running. Using default assistants.",
          `API URL: ${API_URL_STAUTS}`
        );
      } else {
        console.error("Error fetching assistants:", error);
      }
      return this.defaultAssistants;
    }
  }

  async setDefaultAssistant(appID) {
    // Make a POST request to the API to set the default assistant

    const API_URL_STAUTS =
      GlobalVariables.getAPIURL() + UserModel.getEndpoints().defaultAssistant;
    try {
      if (!this.uid) return true; // If no user logged in, it's fine, don't update
      // Build request body, including organizationId if available
      const requestBody = {
        uid: this.uid,
        authEmail: this.email,
        appID: appID || "",
      };

      if (this.organizationId) {
        requestBody.organizationId = this.organizationId;
      }

      const response = await fetch(API_URL_STAUTS, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${this.token}`,
        },
        body: JSON.stringify(requestBody),
      });
      const data = await response.json();
      // console.log("Default assistant set:", data);
      return data?.success;
    } catch (error) {
      console.error("Error setting default assistant:", error);
      return false;
    }
  }

  async getDefaultAssistant() {
    const data = await this.getDefaultAssistantDetails();
    return data?.defaultAssistant || false;
  }

  async getDefaultAssistantDetails() {
    // Make a GET request to the API to get the default assistant
    const API_URL_STAUTS =
      GlobalVariables.getAPIURL() + UserModel.getEndpoints().defaultAssistant;
    try {
      // Build query params, including organizationId if available
      const queryParams = new URLSearchParams({
        uid: this.uid,
        authEmail: encodeURIComponent(this.email),
      });

      if (this.organizationId) {
        queryParams.append("organizationId", this.organizationId);
      }

      const response = await fetch(
        `${API_URL_STAUTS}?${queryParams.toString()}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${this.token}`,
          },
        }
      );
      const data = await response.json();
      return data;
    } catch (error) {
      console.error("Error fetching default assistant:", error);
      return { success: false, defaultAssistant: false };
    }
  }

  async getOrganizationDefaultAssistant() {
    const API_URL_STAUTS =
      GlobalVariables.getAPIURL() +
      UserModel.getEndpoints().organizationDefaultAssistant;
    try {
      if (!this.organizationId || !this.email) {
        return { defaultAssistant: null, canManage: false };
      }

      const queryParams = new URLSearchParams({
        authEmail: this.email,
        organizationId: this.organizationId,
      });

      const response = await fetch(
        `${API_URL_STAUTS}?${queryParams.toString()}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${this.token}`,
            email: this.email,
          },
        }
      );
      const data = await response.json();
      return {
        defaultAssistant: data?.defaultAssistant || null,
        canManage: data?.canManage === true,
      };
    } catch (error) {
      console.error("Error fetching organization default assistant:", error);
      return { defaultAssistant: null, canManage: false };
    }
  }

  async setOrganizationDefaultAssistant(appID) {
    const API_URL_STAUTS =
      GlobalVariables.getAPIURL() +
      UserModel.getEndpoints().organizationDefaultAssistant;
    try {
      if (!this.organizationId || !this.email) return false;

      const response = await fetch(API_URL_STAUTS, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${this.token}`,
          email: this.email,
        },
        body: JSON.stringify({
          uid: this.uid,
          authEmail: this.email,
          organizationId: this.organizationId,
          appID: appID || "",
        }),
      });
      const data = await response.json();
      return data?.success === true;
    } catch (error) {
      console.error("Error setting organization default assistant:", error);
      return false;
    }
  }
}

export default UserModel;
